import { MobileNavigationMenu } from "./MobileNavigationMenu.js";
import { Menu } from "lucide-react";
import { useCallback, useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { matchPath, NavLink, useLocation } from "react-router-dom";
import { navigationForMobile } from "./shell-navigation.js";
import type { Workspace } from "../types/shell.js";

const tabClass = (active: boolean) =>
  `flex min-w-0 flex-1 flex-col items-center gap-0.5 rounded-lg px-2 py-1.5 text-[10px] font-medium focus-visible:outline-none focus-visible:ring-3 focus-visible:ring-ring/25 ${active ? "bg-primary text-primary-foreground" : "text-muted-foreground hover:bg-muted hover:text-foreground"}`;

export function MobileNavigation({ workspace, mentor = false }: { workspace: Workspace; mentor?: boolean }) {
  const { t } = useTranslation();
  const location = useLocation();
  const navigation = navigationForMobile(workspace, mentor);
  const direct = navigation.length > 3 ? navigation.slice(0, 3) : navigation;
  const remaining = navigation.slice(direct.length);
  const [open, setOpen] = useState(false);
  const trigger = useRef<HTMLButtonElement>(null);
  const closeMenu = useCallback(() => {
    setOpen(false);
    trigger.current?.focus();
  }, []);
  const menuId = "mobile-navigation-menu";
  const menuActive = remaining.some((item) =>
    matchPath({ path: item.to, end: item.end ?? false }, location.pathname),
  );

  useEffect(() => {
    setOpen(false);
  }, [location.pathname, location.search, location.hash, workspace, mentor]);

  return (
    <nav
      aria-label={t("nav.mobileNavigation")}
      className="fixed inset-x-3 bottom-3 z-40 rounded-xl border border-border bg-background/95 p-1.5 shadow-lg backdrop-blur md:hidden"
    >
      {open && remaining.length > 0 ? <MobileNavigationMenu items={remaining} onClose={closeMenu} /> : null}
      <div className="flex">
        {direct.map((item) => {
          const Icon = item.icon;
          return (
            <NavLink
              className={({ isActive }) => tabClass(isActive)}
              data-control-id={`${item.controlId}.mobile`}
              end={item.end}
              key={item.to}
              to={item.to}
            >
              <Icon aria-hidden="true" className="size-4" />
              <span className="max-w-full truncate">{t(item.labelKey)}</span>
            </NavLink>
          );
        })}
        {remaining.length > 0 ? (
          <button
            aria-controls={open ? menuId : undefined}
            aria-haspopup="dialog"
            aria-current={menuActive ? "page" : undefined}
            aria-expanded={open}
            className={tabClass(menuActive || open)}
            data-control-id="nav.mobile.menu.toggle"
            onClick={() => setOpen((value) => !value)}
            ref={trigger}
            type="button"
          >
            <Menu aria-hidden="true" className="size-4" />
            <span>{t("nav.menu")}</span>
          </button>
        ) : null}
      </div>
    </nav>
  );
}
